<script lang="ts">
	/**
	 * The one place a `SubscriptionStatus` becomes words (Design §9.1, §14).
	 *
	 * **The status is carried by the text, never by the colour.** Eight provider statuses reduced
	 * to a coloured dot leaves `past_due` reading as "broken" to anyone who can see the amber and
	 * as nothing at all to anyone who cannot — when what it actually means is "a payment failed,
	 * you still have access, and here is the deadline". So every state says what it is in a phrase
	 * a customer can act on, and the colour is decoration on top of that.
	 *
	 * `cancelAtPeriodEnd` overrides the copy rather than adding a second badge: a subscription that
	 * is `active` and cancelling is not two facts to a customer, it is one — "it runs until the end
	 * of the period and then stops".
	 */

	import { Badge } from 'flowbite-svelte';
	import type { SubscriptionStatus } from '../AppTypes';

	export let status: SubscriptionStatus;
	/** True while a cancellation is scheduled. Replaces the copy for the two entitled statuses. */
	export let cancelAtPeriodEnd: boolean = false;
	export let size: 'xs' | 'sm' = 'sm';
	/**
	 * The customer's own screens. Support needs the precise state — "awaiting payment" is what it
	 * looks up at the provider — while the customer needs what it means for them: the purchase went
	 * through and access is on its way, not that something is stuck.
	 */
	export let forCustomer: boolean = false;

	type Tone = 'green' | 'yellow' | 'red' | 'dark' | 'blue';

	/**
	 * Copy per status. Deliberately free of failure vocabulary for the two grace states: `past_due`
	 * and `unpaid` are *entitled* states — the customer still has access — and words like "failed"
	 * would tell them the opposite of what is true.
	 */
	const copy: Record<SubscriptionStatus, { label: string; detail: string; tone: Tone }> = {
		incomplete: {
			label: 'Awaiting payment',
			detail: 'The first payment has not completed yet.',
			tone: 'dark'
		},
		trialing: {
			label: 'Trialing',
			detail: 'Your free trial is running.',
			tone: 'blue'
		},
		active: {
			label: 'Active',
			detail: 'Everything is in order.',
			tone: 'green'
		},
		past_due: {
			label: 'Payment overdue',
			detail: 'A payment did not go through. You still have access while we retry it.',
			tone: 'yellow'
		},
		unpaid: {
			label: 'Payment outstanding',
			detail: 'An invoice is still owing. You still have access while it is settled.',
			tone: 'yellow'
		},
		paused: {
			label: 'Paused',
			detail: 'This subscription is paused and is not being billed.',
			tone: 'dark'
		},
		canceled: {
			label: 'Canceled',
			detail: 'This subscription has been cancelled.',
			tone: 'red'
		},
		expired: {
			label: 'Ended',
			detail: 'This subscription has ended.',
			tone: 'dark'
		}
	};

	const customerIncomplete = {
		label: 'Pending — will be available soon',
		detail: 'Your payment is still being confirmed. Access starts as soon as it clears.',
		tone: 'blue' as Tone
	};

	const scheduledCancellation = {
		label: 'Cancels at period end',
		detail: 'It stays active until the end of the period you have paid for, then stops.',
		tone: 'yellow' as Tone
	};

	// Only the entitled statuses can be *going to* cancel. A subscription that already ended is
	// simply ended, and saying it "cancels at period end" would promise access that is gone.
	$: resolved =
		cancelAtPeriodEnd && (status === 'active' || status === 'trialing')
			? scheduledCancellation
			: forCustomer && status === 'incomplete'
				? customerIncomplete
				: copy[status] ?? copy.expired;
</script>

<Badge color={resolved.tone} rounded class={size === 'xs' ? 'text-[10px]' : ''} title={resolved.detail}>
	{resolved.label}
</Badge>
